import React, { useState } from 'react';
import { DealApplicant, DealAsset, DealLiability } from '../types/api';
import { useOpenInCanvas } from '../contexts/GlobalCanvasContext';

interface OpportunityDetailsProps {
  opportunity: any;
  title?: string;
}

// Mercury deal status colors and progress
const MERCURY_STATUSES: Record<string, { color: string; progress: number }> = {
  'Leads': { color: '#9ca3af', progress: 5 },
  'Information Gathering': { color: '#3b82f6', progress: 10 },
  'Broker Solution Pending': { color: '#f59e0b', progress: 22 },
  'File Preparation': { color: '#ea580c', progress: 35 },
  'Broker Solution': { color: '#8b5cf6', progress: 45 },
  'To be Lodged': { color: '#6366f1', progress: 55 },
  'Lodged': { color: '#06b6d4', progress: 65 },
  'Conditional - Pre Approval': { color: '#14b8a6', progress: 75 },
  'Unconditional': { color: '#10b981', progress: 88 },
  'Pending Settlement': { color: '#059669', progress: 95 },
  'Settled': { color: '#047857', progress: 100 },
  'On Hold': { color: '#6b7280', progress: 0 },
};

// Strip empty HTML from Mercury notes (Mercury sends empty <html><body></body></html> when no notes)
const isEmptyHtml = (text: string): boolean => {
  const stripped = text.replace(/<[^>]*>/g, '').trim();
  return stripped.length === 0;
};

const OpportunityDetails: React.FC<OpportunityDetailsProps> = ({ opportunity, title }) => {
  const [activeTab, setActiveTab] = useState<'overview' | 'financials' | 'documents' | 'timeline'>('overview');
  const { openPerson } = useOpenInCanvas();

  if (!opportunity) {
    return (
      <div className="empty-canvas">
        <span className="material-symbols-outlined">monetization_off</span>
        <h3>Deal Not Found</h3>
        <p>No deal data available</p>
      </div>
    );
  }

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 0
    }).format(amount);
  };

  // Get status info
  const statusInfo = MERCURY_STATUSES[opportunity.status] || { color: '#6b7280', progress: 0 };

  // Extract deal data
  const dealName = opportunity.name || opportunity.opportunityName || title || 'Deal';
  const loanAmount = opportunity.amount || opportunity.loanAmount || 0;
  const transactionType = opportunity.transactionType || '';
  const transactionSubType = opportunity.transactionSubType || '';
  const applicants: DealApplicant[] = opportunity.applicants || [];
  const assets: DealAsset[] = opportunity.assets || [];
  const liabilities: DealLiability[] = opportunity.liabilities || [];
  const mercuryNotes = opportunity.mercuryNotes || '';
  const dealFolder = opportunity.folder || '';

  // Compute totals
  const totalAssets = assets.reduce((sum, a) => sum + (a.value || 0), 0);
  const totalLiabilities = liabilities.reduce((sum, l) => sum + (l.value || 0), 0);
  const netPosition = totalAssets - totalLiabilities;

  return (
    <div style={{
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      background: 'var(--obsidian-bg)'
    }}>
      {/* Header Section */}
      <div style={{
        background: 'var(--obsidian-sidebar-bg)',
        borderBottom: '1px solid var(--obsidian-border)',
        padding: '2rem'
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1.5rem' }}>
          {/* Icon */}
          <div style={{
            width: '80px',
            height: '80px',
            borderRadius: '12px',
            background: `linear-gradient(135deg, ${statusInfo.color}, ${statusInfo.color}99)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}>
            <span className="material-symbols-outlined" style={{ fontSize: '36px', color: 'white' }}>
              monetization_on
            </span>
          </div>

          {/* Header Info */}
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.5rem' }}>
              <h2 style={{ margin: 0, fontSize: '24px', fontWeight: '600' }}>
                {dealName}
              </h2>
              {opportunity.status && (
                <span style={{
                  padding: '4px 12px',
                  borderRadius: '12px',
                  fontSize: '13px',
                  fontWeight: '500',
                  background: `${statusInfo.color}20`,
                  color: statusInfo.color,
                  border: `1px solid ${statusInfo.color}40`
                }}>
                  {opportunity.status}
                </span>
              )}
            </div>

            {/* Type info */}
            {(transactionType || transactionSubType) && (
              <div style={{ fontSize: '14px', color: 'var(--obsidian-text-secondary)', marginBottom: '0.75rem' }}>
                {transactionType}{transactionSubType ? ` - ${transactionSubType}` : ''}
              </div>
            )}

            {/* Key Metrics */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
              gap: '1.5rem',
              marginTop: '0.75rem'
            }}>
              {loanAmount > 0 && (
                <div>
                  <div style={{ fontSize: '12px', color: 'var(--obsidian-text-secondary)', marginBottom: '4px' }}>Loan Amount</div>
                  <div style={{ fontSize: '24px', fontWeight: '700', color: '#10b981' }}>
                    {formatCurrency(loanAmount)}
                  </div>
                </div>
              )}
              {applicants.length > 0 && (
                <div>
                  <div style={{ fontSize: '12px', color: 'var(--obsidian-text-secondary)', marginBottom: '4px' }}>Applicants</div>
                  <div style={{ fontSize: '14px', fontWeight: '500' }}>
                    {applicants.map(a => a.name).join(', ')}
                  </div>
                </div>
              )}
              {dealFolder && (
                <div>
                  <div style={{ fontSize: '12px', color: 'var(--obsidian-text-secondary)', marginBottom: '4px' }}>Folder</div>
                  <div style={{ fontSize: '14px', fontWeight: '500' }}>{dealFolder}</div>
                </div>
              )}
            </div>

            {/* Progress Bar */}
            {opportunity.status && (
              <div style={{ marginTop: '1.5rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                  <span style={{ fontSize: '12px', fontWeight: '500' }}>Deal Progress</span>
                  <span style={{ fontSize: '12px', color: 'var(--obsidian-text-secondary)' }}>{statusInfo.progress}%</span>
                </div>
                <div style={{
                  width: '100%',
                  height: '8px',
                  background: 'var(--obsidian-bg)',
                  borderRadius: '4px',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    height: '100%',
                    width: `${statusInfo.progress}%`,
                    background: `linear-gradient(90deg, ${statusInfo.color}, ${statusInfo.color}CC)`,
                    transition: 'width 300ms ease'
                  }} />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div style={{
        display: 'flex',
        gap: '0.5rem',
        padding: '1rem 2rem 0',
        borderBottom: '1px solid var(--obsidian-border)',
        background: 'var(--obsidian-bg)'
      }}>
        {[
          { id: 'overview', label: 'Overview', icon: 'dashboard' },
          { id: 'financials', label: 'Financials', icon: 'payments' },
          { id: 'documents', label: 'Documents', icon: 'folder' },
          { id: 'timeline', label: 'Timeline', icon: 'timeline' }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as any)}
            style={{
              padding: '0.75rem 1rem',
              border: 'none',
              background: activeTab === tab.id ? 'var(--obsidian-sidebar-bg)' : 'transparent',
              color: activeTab === tab.id ? 'var(--obsidian-text-primary)' : 'var(--obsidian-text-secondary)',
              borderBottom: activeTab === tab.id ? '2px solid var(--obsidian-accent)' : '2px solid transparent',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              transition: 'all 150ms'
            }}
          >
            <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>{tab.icon}</span>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content Area */}
      <div style={{
        flex: 1,
        overflow: 'auto',
        padding: '2rem'
      }}>
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.5rem' }}>
              {/* Deal Details Card */}
              <div style={{
                background: 'var(--obsidian-sidebar-bg)',
                border: '1px solid var(--obsidian-border)',
                borderRadius: '8px',
                padding: '1.5rem'
              }}>
                <h3 style={{ margin: '0 0 1rem 0', fontSize: '16px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span className="material-symbols-outlined">info</span>
                  Deal Details
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  {transactionType && (
                    <DetailRow label="Transaction Type" value={transactionType} />
                  )}
                  {transactionSubType && (
                    <DetailRow label="Sub Type" value={transactionSubType} />
                  )}
                  {opportunity.status && (
                    <DetailRow label="Status" value={opportunity.status} />
                  )}
                  {loanAmount > 0 && (
                    <DetailRow label="Loan Amount" value={formatCurrency(loanAmount)} />
                  )}
                  {dealFolder && (
                    <DetailRow label="Deal Folder" value={dealFolder} />
                  )}
                  {opportunity.mercuryId && (
                    <DetailRow label="Mercury ID" value={opportunity.mercuryId} />
                  )}
                </div>
              </div>

              {/* Applicants Card */}
              {applicants.length > 0 && (
                <div style={{
                  background: 'var(--obsidian-sidebar-bg)',
                  border: '1px solid var(--obsidian-border)',
                  borderRadius: '8px',
                  padding: '1.5rem'
                }}>
                  <h3 style={{ margin: '0 0 1rem 0', fontSize: '16px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span className="material-symbols-outlined">group</span>
                    Applicants
                  </h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    {applicants.map((applicant, i) => {
                      const initials = (applicant.name || '?')
                        .split(' ')
                        .map(n => n[0])
                        .join('')
                        .toUpperCase()
                        .slice(0, 2);

                      return (
                        <div
                          key={i}
                          onClick={() => openPerson({
                            id: applicant.mercuryContactId || applicant.name,
                            name: applicant.name,
                            mercuryId: applicant.mercuryContactId,
                            status: applicant.partyType || applicant.role || 'Applicant'
                          })}
                          style={{
                            display: 'flex', alignItems: 'center', gap: '12px',
                            padding: '10px 14px',
                            backgroundColor: 'var(--obsidian-bg)',
                            border: '1px solid var(--obsidian-border)',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.borderColor = 'var(--obsidian-accent)';
                            e.currentTarget.style.transform = 'translateX(2px)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.borderColor = 'var(--obsidian-border)';
                            e.currentTarget.style.transform = 'translateX(0)';
                          }}
                        >
                          <div style={{
                            width: '36px', height: '36px', borderRadius: '50%',
                            backgroundColor: 'var(--obsidian-accent)',
                            color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: '13px', fontWeight: '600', flexShrink: 0
                          }}>
                            {initials}
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: '14px', fontWeight: '500' }}>{applicant.name}</div>
                            {applicant.mercuryContactId && (
                              <div style={{ fontSize: '11px', color: 'var(--obsidian-text-secondary)', marginTop: '2px' }}>
                                Mercury ID: {applicant.mercuryContactId.slice(0, 8)}...
                              </div>
                            )}
                          </div>
                          <span style={{
                            padding: '3px 10px', borderRadius: '10px', fontSize: '12px', fontWeight: '500',
                            background: 'var(--obsidian-bg)', color: 'var(--obsidian-text-secondary)',
                            border: '1px solid var(--obsidian-border)'
                          }}>
                            {applicant.partyType || applicant.role || 'Applicant'}
                          </span>
                          <span className="material-symbols-outlined" style={{ fontSize: '16px', color: 'var(--obsidian-text-disabled)' }}>
                            chevron_right
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Mercury Notes Card */}
            {mercuryNotes && !isEmptyHtml(mercuryNotes) && (
              <div style={{
                background: 'var(--obsidian-sidebar-bg)',
                border: '1px solid var(--obsidian-border)',
                borderRadius: '8px',
                padding: '1.5rem'
              }}>
                <h3 style={{ margin: '0 0 1rem 0', fontSize: '16px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span className="material-symbols-outlined">note</span>
                  Mercury CRM Notes
                </h3>
                <pre style={{
                  margin: 0,
                  padding: '1rem',
                  background: 'var(--obsidian-bg)',
                  borderRadius: '6px',
                  border: '1px solid var(--obsidian-border)',
                  fontSize: '13px',
                  lineHeight: '1.6',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  fontFamily: 'inherit',
                  color: 'var(--obsidian-text-primary)',
                  maxHeight: '300px',
                  overflow: 'auto'
                }}>
                  {mercuryNotes}
                </pre>
              </div>
            )}
          </div>
        )}

        {/* Financials Tab */}
        {activeTab === 'financials' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            {/* Summary Cards */}
            {(totalAssets > 0 || totalLiabilities > 0 || loanAmount > 0) && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                {loanAmount > 0 && (
                  <SummaryCard label="Loan Amount" value={formatCurrency(loanAmount)} color="#3b82f6" icon="account_balance" />
                )}
                {totalAssets > 0 && (
                  <SummaryCard label="Total Assets" value={formatCurrency(totalAssets)} color="#10b981" icon="trending_up" />
                )}
                {totalLiabilities > 0 && (
                  <SummaryCard label="Total Liabilities" value={formatCurrency(totalLiabilities)} color="#ef4444" icon="trending_down" />
                )}
                {(totalAssets > 0 || totalLiabilities > 0) && (
                  <SummaryCard
                    label="Net Position"
                    value={formatCurrency(netPosition)}
                    color={netPosition >= 0 ? '#10b981' : '#ef4444'}
                    icon="balance"
                  />
                )}
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '1.5rem' }}>
              {/* Assets Table */}
              {assets.length > 0 && (
                <div style={{
                  background: 'var(--obsidian-sidebar-bg)',
                  border: '1px solid var(--obsidian-border)',
                  borderRadius: '8px',
                  padding: '1.5rem'
                }}>
                  <h3 style={{ margin: '0 0 1rem 0', fontSize: '16px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span className="material-symbols-outlined">trending_up</span>
                    Assets ({assets.length})
                  </h3>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid var(--obsidian-border)' }}>
                        <th style={thStyle}>Name</th>
                        <th style={thStyle}>Type</th>
                        <th style={{ ...thStyle, textAlign: 'right' }}>Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {assets.map((asset, i) => (
                        <tr key={i} style={{ borderBottom: '1px solid var(--obsidian-border)' }}>
                          <td style={tdStyle}>{asset.name || '-'}</td>
                          <td style={{ ...tdStyle, color: 'var(--obsidian-text-secondary)' }}>{asset.type || '-'}</td>
                          <td style={{ ...tdStyle, textAlign: 'right', fontWeight: '500', color: '#10b981' }}>
                            {asset.value ? formatCurrency(asset.value) : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan={2} style={{ ...tdStyle, fontWeight: '600' }}>Total</td>
                        <td style={{ ...tdStyle, textAlign: 'right', fontWeight: '700', color: '#10b981', fontSize: '15px' }}>
                          {formatCurrency(totalAssets)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}

              {/* Liabilities Table */}
              {liabilities.length > 0 && (
                <div style={{
                  background: 'var(--obsidian-sidebar-bg)',
                  border: '1px solid var(--obsidian-border)',
                  borderRadius: '8px',
                  padding: '1.5rem'
                }}>
                  <h3 style={{ margin: '0 0 1rem 0', fontSize: '16px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span className="material-symbols-outlined">trending_down</span>
                    Liabilities ({liabilities.length})
                  </h3>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid var(--obsidian-border)' }}>
                        <th style={thStyle}>Category</th>
                        <th style={thStyle}>Type</th>
                        <th style={{ ...thStyle, textAlign: 'right' }}>Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {liabilities.map((liability, i) => (
                        <tr key={i} style={{ borderBottom: '1px solid var(--obsidian-border)' }}>
                          <td style={tdStyle}>
                            {liability.name || '-'}
                            {liability.details && (
                              <div style={{ fontSize: '11px', color: 'var(--obsidian-text-secondary)', marginTop: '2px' }}>
                                {liability.details}
                              </div>
                            )}
                          </td>
                          <td style={{ ...tdStyle, color: 'var(--obsidian-text-secondary)' }}>{liability.type || '-'}</td>
                          <td style={{ ...tdStyle, textAlign: 'right', fontWeight: '500', color: '#ef4444' }}>
                            {liability.value ? formatCurrency(liability.value) : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan={2} style={{ ...tdStyle, fontWeight: '600' }}>Total</td>
                        <td style={{ ...tdStyle, textAlign: 'right', fontWeight: '700', color: '#ef4444', fontSize: '15px' }}>
                          {formatCurrency(totalLiabilities)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>

            {/* Net Position Card */}
            {(totalAssets > 0 || totalLiabilities > 0) && (
              <div style={{
                background: netPosition >= 0 ? 'rgba(16,185,129,0.08)' : 'rgba(239,68,68,0.08)',
                border: `1px solid ${netPosition >= 0 ? 'rgba(16,185,129,0.3)' : 'rgba(239,68,68,0.3)'}`,
                borderRadius: '8px',
                padding: '1.5rem',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div>
                  <div style={{ fontSize: '14px', fontWeight: '600' }}>Net Position (Assets - Liabilities)</div>
                  <div style={{ fontSize: '12px', color: 'var(--obsidian-text-secondary)', marginTop: '4px' }}>
                    {assets.length} asset{assets.length !== 1 ? 's' : ''} | {liabilities.length} liabilit{liabilities.length !== 1 ? 'ies' : 'y'}
                  </div>
                </div>
                <div style={{
                  fontSize: '24px',
                  fontWeight: '700',
                  color: netPosition >= 0 ? '#10b981' : '#ef4444'
                }}>
                  {formatCurrency(netPosition)}
                </div>
              </div>
            )}

            {/* Empty state if no financial data */}
            {assets.length === 0 && liabilities.length === 0 && loanAmount === 0 && (
              <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--obsidian-text-secondary)' }}>
                <span className="material-symbols-outlined" style={{ fontSize: '48px', opacity: 0.3 }}>payments</span>
                <p>No financial data available. Sync with Mercury to pull assets and liabilities.</p>
              </div>
            )}
          </div>
        )}

        {/* Documents Tab */}
        {activeTab === 'documents' && (
          <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--obsidian-text-secondary)' }}>
            <span className="material-symbols-outlined" style={{ fontSize: '48px', opacity: 0.3 }}>folder_open</span>
            <p>Document management coming soon</p>
          </div>
        )}

        {/* Timeline Tab */}
        {activeTab === 'timeline' && (
          <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--obsidian-text-secondary)' }}>
            <span className="material-symbols-outlined" style={{ fontSize: '48px', opacity: 0.3 }}>timeline</span>
            <p>Deal timeline coming soon</p>
          </div>
        )}
      </div>
    </div>
  );
};

// Detail row component
const DetailRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div>
    <div style={{ fontSize: '12px', color: 'var(--obsidian-text-secondary)', marginBottom: '4px' }}>{label}</div>
    <div style={{ fontSize: '14px', fontWeight: '500' }}>{value}</div>
  </div>
);

// Summary card component
const SummaryCard: React.FC<{ label: string; value: string; color: string; icon: string }> = ({ label, value, color, icon }) => (
  <div style={{
    background: 'var(--obsidian-sidebar-bg)',
    border: '1px solid var(--obsidian-border)',
    borderRadius: '8px',
    padding: '1.25rem',
    display: 'flex',
    alignItems: 'center',
    gap: '1rem'
  }}>
    <div style={{
      width: '40px', height: '40px', borderRadius: '8px',
      background: `${color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center'
    }}>
      <span className="material-symbols-outlined" style={{ fontSize: '22px', color }}>{icon}</span>
    </div>
    <div>
      <div style={{ fontSize: '12px', color: 'var(--obsidian-text-secondary)' }}>{label}</div>
      <div style={{ fontSize: '18px', fontWeight: '700', color }}>{value}</div>
    </div>
  </div>
);

// Table styles
const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '0.5rem 0.5rem',
  fontSize: '12px',
  color: 'var(--obsidian-text-secondary)',
  fontWeight: '500'
};

const tdStyle: React.CSSProperties = {
  padding: '0.5rem 0.5rem',
  fontSize: '14px'
};

export default OpportunityDetails;
